import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import opLinuxImg from '../op_Linux_img.png';
import solRhelImg from '../sol_RHEL_img.jpg';
import solJbossImg from '../sol_JBoss_img.jpg';
import solOpenShiftImg from '../sol_OpenShift_img.jpg';
import solLenaImg from '../sol_LENA_img.jpg';
import solResinImg from '../sol_Resin_img.jpg';
import solAgensSqlImg from '../sol_agensSql_img.jpg';
import solOraTibImg from '../sol_ORTI_img.jpg';
import solClusterPlexImg from '../sol_clusterPlex_img.jpg';
import { addMobileDetectionListener } from '../utils/deviceDetection';
import '../App.css';

const SolutionsPage = () => {
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState('lena');
  const [isMobile, setIsMobile] = useState(false);
  
  // 각 섹션에 대한 ref 생성
  const redhatRef = useRef(null);
  const lenaRef = useRef(null);
  const resinRef = useRef(null);
  const agenssqlRef = useRef(null);
  const oracletiberoRef = useRef(null);
  const clusterplexRef = useRef(null);

  const categories = [
    { id: 'lena', name: 'LENA', title: 'LENA Solution' },
    { id: 'redhat', name: 'RedHat', title: 'Red Hat Enterprise Linux' },
    { id: 'resin', name: 'Resin', title: 'Resin Application Server' },
    { id: 'agenssql', name: 'AgensSQL', title: 'AgensSQL Database' },
    { id: 'oracletibero', name: 'Oracle/Tibero', title: 'Oracle/Tibero Database' },
    { id: 'clusterplex', name: 'ClusterPlex', title: 'ClusterPlex Solution' }
  ];

  const refs = {
    redhat: redhatRef,
    lena: lenaRef,
    resin: resinRef,
    agenssql: agenssqlRef,
    oracletibero: oracletiberoRef,
    clusterplex: clusterplexRef
  };

  // 모바일 감지 설정
  useEffect(() => {
    const cleanupMobileListener = addMobileDetectionListener(setIsMobile);
    return () => {
      cleanupMobileListener();
    };
  }, []);

  // URL 파라미터 확인하여 초기 카테고리 설정
  useEffect(() => {
    // 페이지 로드 시 스크롤을 맨 위로 이동
    window.scrollTo(0, 0);
    
    const params = new URLSearchParams(location.search);
    const category = params.get('category');
    if (category && categories.find(cat => cat.id === category)) {
      setActiveCategory(category);
      // 페이지 로드 후 해당 섹션으로 스크롤
      setTimeout(() => {
        const targetRef = refs[category];
        if (targetRef && targetRef.current) {
          const headerOffset = isMobile ? 70 : 88;
          const elementPosition = targetRef.current.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      setActiveCategory('lena');
    }
  }, [location.search, isMobile]);

  // Intersection Observer 설정 (데스크톱에서만 실행)
  useEffect(() => {
    if (isMobile) return; // 모바일에서는 Intersection Observer 비활성화

    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px', // 상단 20%, 하단 70% 위치에서 트리거
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // 현재 보이는 섹션의 카테고리 ID 찾기
          const categoryId = Object.keys(refs).find(key => 
            refs[key].current === entry.target
          );
          if (categoryId) {
            setActiveCategory(categoryId);
          }
        }
      });
    }, observerOptions);

    // 모든 섹션을 관찰 대상으로 등록
    Object.values(refs).forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [isMobile]);

  const handleCategoryClick = (categoryId) => {
    setActiveCategory(categoryId);
    const targetRef = refs[categoryId];
    if (targetRef && targetRef.current) {
      // 상단 고정 헤더 높이 + 여유 공간
      const headerOffset = isMobile ? 70 : 88;
      const elementPosition = targetRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className={`subcustom-header-container ${isMobile ? 'mobile-device' : ''}`}>
      <header className="subcustom-header">
        <div className="hero-title-area">
          <div className="hero-subtitle">SOLUTIONS</div>
          <h2 className="hero-title">인프라솔루션</h2>
        </div>
      </header>

      <main className="infra-main">
        {/* 모바일에서는 카테고리 탭 숨김 */}

        <div className="solutions-layout">
          {/* 왼쪽 사이드바 카테고리 메뉴 - 데스크톱에서만 표시 */}
          {!isMobile && (
            <div className="sidebar-categories">
              <h3 className="sidebar-title">인프라솔루션</h3>
              <ul className="category-menu">
                {categories.map(category => (
                  <li key={category.id}>
                    <button
                      className={`category-item ${activeCategory === category.id ? 'active' : ''}`}
                      onClick={() => handleCategoryClick(category.id)}
                    >
                      {category.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 콘텐츠 영역 */}
          <div className="content-area">
            {/* LENA 솔루션 */}
            <div className="solution-section" ref={lenaRef}>
              <h2 className="section-title">LENA 1.3.1</h2>
              <div className="product-content">
                <div className="product-text">
                  <p>Cloud 환경에 최적화된 고효율 차세대 WEB/WAS</p>
                  <div className="feature-list">
                    <h3>주요 특징:</h3>
                    <ul>
                      <li>LG CNS 제조사 공식 Partner</li>
                      <li>IT비용절감/벤더 종속성 해결/운영 효율성</li>
                      <li>GS인증제품</li>
                      <li>전자정부프레임웍 호환성 인증</li>
                      <li>나라장터 디지털서비스몰 등록</li>
                    </ul>
                  </div>
                </div>
                <div className="product-image">
                  <img src={solLenaImg} alt="LENA" />
                </div>
              </div>
            </div>

            {/* RedHat 솔루션 */}
            <div className="solution-section" ref={redhatRef}>
              <h2 className="section-title">Red Hat Solutions</h2>
              <div className="redhat-content">
                <div className="product-content">
                  <div className="product-text">
                    <h3 className="product-title">Red Hat Enterprise Linux 10</h3>
                    <div className="feature-list">
                      <h4>주요특징:</h4>
                      <ul>
                        <li>RedHat 제품 공식 Partner</li>
                        <li>전세계 및 국내 압도적인 시장 점유율 1위</li>
                        <li>타 제품과 비교하여 안정성, 보안성 우수</li>
                        <li>Subscription 기반 충실한 기술지원</li>
                        <li>저비용 고효율 IT시스템 구축</li>
                        <li>서브스크립션 계약에 따른 충실한 기술지원</li>
                      </ul>
                    </div>
                  </div>
                  <div className="product-image">
                    <img src={solRhelImg} alt="Red Hat Enterprise Linux" />
                  </div>
                </div>
                
                <div className="product-content">
                  <div className="product-text">
                    <h3 className="product-title">JBoss EAP 8.0</h3>
                    <div className="feature-list">
                      <h4>주요특징:</h4>
                      <ul>
                        <li>RedHat 제품 공식 Partner</li>
                        <li>클라우드 컨테이너에 최적화된 솔루션</li>
                        <li>경량화된 유연한 아키텍처</li>
                        <li>OpenShift에 맞게 최적화 됨</li>
                        <li>마이크로서비스 개발지원 및 표준화</li>
                        <li>서브스크립션 계약에 따른 기술지원</li>
                      </ul>
                    </div>
                  </div>
                  <div className="product-image">
                    <img src={solJbossImg} alt="JBoss EAP" />
                  </div>
                </div>
                
                <div className="product-content">
                  <div className="product-text">
                    <h3 className="product-title">OpenShift Virtualization<br />OpenShift Container Platform</h3>
                    <div className="feature-list">
                      <h4>주요특징:</h4>
                      <ul>
                        <li>RedHat 제품 공식 Partner</li>
                        <li>Brodcom VMware 제품 대안</li>
                        <li>Docker와 Kubernetes를 제공하는 컨테이너 애플리케이션 플랫폼</li>
                        <li>가상머신과 컨테이너 통합으로 인한 효율성 증대 비용절감</li>
                        <li>중앙화된 정책관리</li>
                        <li>에코시스템 통합</li>
                      </ul>
                    </div>
                  </div>
                  <div className="product-image">
                    <img src={solOpenShiftImg} alt="RedHat OpenShift" />
                  </div>
                </div>
              </div>
            </div>

            {/* Resin 솔루션 */}
            <div className="solution-section" ref={resinRef}>
              <h2 className="section-title">Resin professional 4.0</h2>
              <div className="product-content">
                <div className="product-text">
                  <p>고성능 Java 애플리케이션 서버 Resin 솔루션입니다.</p>
                  <div className="feature-list">
                    <h3>주요 특징:</h3>
                    <ul>
                      <li>국내 단독 총판사</li>
                      <li>기본 Enterprise 제공</li>
                      <li>국내 20년 이상 1200+ 레퍼런스</li>
                      <li>GS인증제품</li>
                      <li>전자정부프레임웍 호환성 인증</li>
                      <li>나라장터 디지털서비스몰 등록</li>
                    </ul>
                  </div>
                </div>
                <div className="product-image">
                  <img src={solResinImg} alt="Resin" />
                </div>
              </div>
            </div>

            {/* AgensSQL 솔루션 */}
            <div className="solution-section" ref={agenssqlRef}>
              <h2 className="section-title">AgensSQL Enterprise/GanAI</h2>
              <div className="product-content">
                <div className="product-text">
                  <p>비트나인 제조사 공식 제품, 기술 파트너</p>
                  <div className="feature-list">
                    <h3>주요 특징:</h3>
                    <ul>
                      <li>오픈소스 PostgreSQL의 Core 엔진 작업을 통한 최적화 과정을 거친 상용 DBMS</li>
                      <li>오픈소스 DB 제품에 책임소재, 제품의 성능 품질보증 문제해결</li>
                      <li>고비용 상용DBMS Migration 비용절감</li>
                      <li>GS인증제품</li>
                      <li>나라장터 디지털서비스몰 등록</li>
                    </ul>
                  </div>
                </div>
                <div className="product-image">
                  <img src={solAgensSqlImg} alt="AgensSQL Database" />
                </div>
              </div>
            </div>

            {/* Oracle/Tibero 솔루션 */}
            <div className="solution-section" ref={oracletiberoRef}>
              <h2 className="section-title">Oracle/Tibero</h2>
              <div className="product-content">
                <div className="product-text">
                  <div className="feature-list">
                    <h3>주요 특징:</h3>
                    <ul>
                      <li>Oracle/Tibero 제품공급 및 기술지원</li>
                      <li>24x365 유지보수 서비스</li>
                      <li>DPM 솔루션을 통한 SQL 성능가이드</li>
                      <li>DBMS 성능컨설팅(별도 협의가능)</li>
                      <li>DBA업무지원(별도 협의가능)</li>
                      <li>체계적인 유지관리 서비스 제공<br />(제품지원, 장애지원, 교육지원, 정기점검)</li>
                    </ul>
                  </div>
                </div>
                <div className="product-image">
                  <img src={solOraTibImg} alt="Oracle/Tibero Database" />
                </div>
              </div>
            </div>

            {/* ClusterPlex 솔루션 */}
            <div className="solution-section" ref={clusterplexRef}>
              <h2 className="section-title">ClusterPlex V5</h2>
              <div className="product-content">
                <div className="product-text">
                  <p>에스엠인포메이션 제조사 공식 파트너</p>
                  <div className="feature-list">
                    <h3>주요 특징:</h3>
                    <ul>
                      <li>서비스 연속성 보장/데이터 안정성 지원/데이터 백업 및 DR 구성</li>
                      <li>MS Hyper V 상의 Guest VM(전체 또는 선택)을 실시간 복제하여 장애에 대비하는 <br/>구성(A-A/A-S 구성 국내 유일 솔루션)</li>
                      <li>Active/Active 구성으로 효율적 활용이 가능하며, Failback을 할 필요 없음</li>
                      <li>Target VM 용 App. S/W 설치 필요 없음</li>
                      <li>GS인증제품</li>
                      <li>나라장터 디지털서비스몰 등록</li>
                    </ul>
                  </div>
                </div>
                <div className="product-image">
                  <img src={solClusterPlexImg} alt="ClusterPlex Solution" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SolutionsPage; 